
import { 
    doc, setDoc, getDoc, updateDoc, 
    collection, addDoc, getDocs, query, orderBy, limit, deleteDoc
} from 'firebase/firestore';
import { ref, uploadString, getDownloadURL } from 'firebase/storage';
import { db, storage, isConfigured } from './firebaseConfig';
import { 
    CareerPath, CareerPrototypeDoc, JourneyEntry, Message, 
    ChatSummary, UserProfile
} from '../types';

// Helper: Sanitize for Firestore (remove undefined)
const sanitizeForFirestore = (obj: any): any => {
    return JSON.parse(JSON.stringify(obj));
};

// Helper: ID generator
const toId = (text: string) => text.toLowerCase().replace(/[^a-z0-9]+/g, '-');

// Helper: Upload Image to Storage (Strict Enforcement)
const ensureImageInStorage = async (uid: string, imageData: string | undefined, folder: string): Promise<string | null> => {
    if (!imageData) return null;
    
    // If it's already a URL, return it
    if (imageData.startsWith('http')) return imageData;
    
    // If it's a Base64 string, upload it
    if (imageData.startsWith('data:image') && storage) {
        try {
            console.log(`📤 Uploading image to Storage (${folder})...`);
            const timestamp = Date.now();
            const randomId = Math.random().toString(36).substring(7);
            const fileName = `users/${uid}/${folder}/${timestamp}_${randomId}.png`;
            const storageRef = ref(storage, fileName);
            
            await uploadString(storageRef, imageData, 'data_url');
            const url = await getDownloadURL(storageRef);
            console.log("✅ Image persisted to Storage:", url);
            return url;
        } catch (error) {
            console.error("❌ Failed to upload image to Storage:", error);
            // Critical: Do NOT return the Base64 string as fallback. 
            // It will crash Firestore. Return null to fail gracefully.
            return null;
        }
    }

    // If we have data but no storage config, we can't save it safely.
    if (imageData.length > 100000) {
        console.warn("⚠️ Image too large for Firestore and Storage not configured. Dropping image.");
        return null;
    }

    return imageData;
};

// --- USER PROFILE ---

export const saveUserProfile = async (uid: string, profile: Partial<UserProfile>) => {
    if (!isConfigured || !db) return;
    try {
        await setDoc(doc(db, "users", uid), sanitizeForFirestore(profile), { merge: true });
    } catch (e) {
        console.error("Error saving profile:", e);
    }
};

export const loadUserProfile = async (uid: string): Promise<UserProfile | null> => {
    if (!isConfigured || !db) return null;
    try {
        const snap = await getDoc(doc(db, "users", uid));
        if (snap.exists()) {
            return snap.data() as UserProfile;
        }
    } catch (e) {
        console.error("Error loading profile:", e);
    }
    return null;
};

export const deleteUserProfile = async (uid: string) => {
    if (!isConfigured || !db) return;
    try {
        await deleteDoc(doc(db, "users", uid));
    } catch (e) {
        console.error("Error deleting profile:", e);
    }
};

// --- CAREER PROTOTYPES ---

export const saveCareerPrototype = async (uid: string, career: CareerPath | CareerPrototypeDoc) => {
    if (!isConfigured || !db) return;
    const docId = toId(career.title);
    
    // Construct Doc (Handling both Path and Doc types)
    const docData: CareerPrototypeDoc = {
        id: docId,
        title: career.title,
        description: career.description,
        matchScore: career.matchScore,
        reasoning: career.reasoning,
        bucketMatches: career.bucketMatches,
        createdAt: (career as any).createdAt || Date.now(),
        // Preserve existing modules if passed in full doc
        visionBoard: (career as any).visionBoard,
        skillTree: (career as any).skillTree,
        simulation: (career as any).simulation,
        globalHubs: (career as any).globalHubs,
        gapAnalysis: (career as any).gapAnalysis
    };

    // Ensure vision board image is in storage if it exists in the incoming object
    if (docData.visionBoard?.imageUrl) {
        const secureUrl = await ensureImageInStorage(uid, docData.visionBoard.imageUrl, 'visions');
        if (secureUrl) {
            docData.visionBoard.imageUrl = secureUrl;
        } else {
            // If upload failed or invalid, remove the image to protect DB
            delete docData.visionBoard;
        }
    }

    try {
        await setDoc(doc(db, "users", uid, "prototypes", docId), sanitizeForFirestore(docData), { merge: true });
    } catch (e) {
        console.error("Error saving prototype:", e);
    }
};

export const getCareerPrototypes = async (uid: string): Promise<CareerPrototypeDoc[]> => {
    if (!isConfigured || !db) return [];
    try {
        const q = query(collection(db, "users", uid, "prototypes"), orderBy("createdAt", "desc"));
        const snap = await getDocs(q);
        return snap.docs.map(d => d.data() as CareerPrototypeDoc);
    } catch (e) {
        console.error("Error getting prototypes:", e);
        return [];
    }
};

export const updatePrototypeModule = async (uid: string, careerTitle: string, moduleName: string, data: any) => {
    if (!isConfigured || !db) return;
    const docId = toId(careerTitle);
    
    let payload;
    
    // Special handling for Vision Board which uses 'imageUrl' key instead of 'data'
    if (moduleName === 'visionBoard') {
        // Enforce Storage Upload
        const secureUrl = await ensureImageInStorage(uid, data, 'visions');
        
        if (!secureUrl) {
            console.error("⚠️ Aborting vision board save: Image upload failed.");
            return; // Stop. Do not write to DB.
        }

        payload = { imageUrl: secureUrl, generatedAt: Date.now() };
    } else {
        payload = { data: data, generatedAt: Date.now() };
    }

    try {
        await updateDoc(doc(db, "users", uid, "prototypes", docId), {
            [`${moduleName}`]: sanitizeForFirestore(payload)
        });
    } catch (e) {
        console.error(`Error updating module ${moduleName}:`, e);
    }
};

// --- JOURNEY TIMELINE ---

export const addJourneyEntry = async (uid: string, entry: Omit<JourneyEntry, 'id' | 'createdAt'>) => {
    if (!isConfigured || !db) return;
    try {
        const safeData = { ...entry.data };

        // Check for Image in data (Vision Boards)
        if (entry.subtype === 'vision_board' && safeData.imageUrl) {
            const secureUrl = await ensureImageInStorage(uid, safeData.imageUrl, 'journey');
            if (secureUrl) {
                safeData.imageUrl = secureUrl;
            } else {
                // Remove image if upload failed to prevent DB crash
                delete safeData.imageUrl; 
                safeData.error = "Image upload failed";
            }
        }

        const newEntry: JourneyEntry = {
            ...entry,
            data: safeData,
            id: Date.now().toString(),
            createdAt: Date.now()
        };
        await addDoc(collection(db, "users", uid, "journey"), sanitizeForFirestore(newEntry));
    } catch (e) {
        console.error("Error adding journey entry:", e);
    }
};

export const getJourneyTimeline = async (uid: string): Promise<JourneyEntry[]> => {
    if (!isConfigured || !db) return [];
    try {
        const q = query(collection(db, "users", uid, "journey"), orderBy("createdAt", "desc"), limit(50));
        const snap = await getDocs(q);
        return snap.docs.map(d => ({ id: d.id, ...d.data() } as JourneyEntry));
    } catch (e) {
        console.error("Error getting journey:", e);
        return [];
    }
};

// --- CHAT HISTORY ---

export const addMessage = async (uid: string, conversationId: string, message: Message) => {
    if (!isConfigured || !db) return;
    try {
        // Store messages in subcollection: users/{uid}/conversations/{conversationId}/messages/{messageId}
        await setDoc(doc(db, "users", uid, "conversations", conversationId, "messages", message.id), sanitizeForFirestore(message));
    } catch (e) {
        console.error("Error adding message:", e);
    }
};

export const getChatHistory = async (uid: string, conversationId: string): Promise<Message[]> => {
    if (!isConfigured || !db) return [];
    try {
        const q = query(collection(db, "users", uid, "conversations", conversationId, "messages"), orderBy("createdAt", "asc"));
        const snap = await getDocs(q);
        return snap.docs.map(d => d.data() as Message);
    } catch (e) {
        console.error("Error loading chat:", e);
        return [];
    }
};

export const updateChatMessage = async (uid: string, conversationId: string, messageId: string, updates: Partial<Message>) => {
    if (!isConfigured || !db) return;
    try {
        await updateDoc(doc(db, "users", uid, "conversations", conversationId, "messages", messageId), sanitizeForFirestore(updates));
    } catch (e) {
        console.error("Error updating message:", e);
    }
}

// --- SUMMARIES ---

export const getChatSummaries = async (uid: string, limitCount: number = 5): Promise<ChatSummary[]> => {
    if (!isConfigured || !db) return [];
    try {
        const q = query(collection(db, "users", uid, "summaries"), orderBy("createdAt", "desc"), limit(limitCount));
        const snap = await getDocs(q);
        return snap.docs.map(d => d.data() as ChatSummary);
    } catch (e) {
        return [];
    }
};

export const saveChatSummary = async (uid: string, summary: Omit<ChatSummary, 'id' | 'createdAt'>) => {
    if (!isConfigured || !db) return;
    try {
        const newSummary: ChatSummary = {
            ...summary,
            id: Date.now().toString(),
            createdAt: Date.now()
        };
        await addDoc(collection(db, "users", uid, "summaries"), sanitizeForFirestore(newSummary));
    } catch (e) {
        console.error("Error saving summary:", e);
    }
};

// --- MIGRATION UTILS ---
export const migrateLegacyChats = async (_uid: string) => {
    // Placeholder for future migration logic if we move from local storage or single-doc chat
    return; 
};
